import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTranscriptById, getStatusLabel, getTypeLabel, formatDuration } from '../data/mockData';
import { formatDateTime } from '../utils/persian';

type AiTab = 'summary' | 'tasks' | 'translation' | 'chapters';

export const TranscriptDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isBackendAvailable, isDemo } = useAuth();
  const [activeTab, setActiveTab] = useState<AiTab>('summary');

  const transcript = id ? getTranscriptById(id) : undefined;

  if (!transcript) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={styles.emptyIcon}>📄</p>
          <p style={styles.emptyText}>رونویسی مورد نظر یافت نشد</p>
          <button onClick={() => navigate('/history')} style={styles.backButton}>
            بازگشت به تاریخچه
          </button>
        </div>
      </div>
    );
  }

  const hasAiOutputs = transcript.status === 'completed' && transcript.ai_outputs?.short_summary;

  const tabs: { key: AiTab; label: string }[] = [
    { key: 'summary', label: 'خلاصه' },
    { key: 'tasks', label: 'وظایف' },
    { key: 'translation', label: 'ترجمه' },
    { key: 'chapters', label: 'فصل‌ها' },
  ];

  const renderAiTab = () => {
    if (!hasAiOutputs) {
      return (
        <div style={styles.emptySection}>
          <p>خروجی هوش مصنوعی در دسترس نیست.</p>
          {transcript.status === 'processing' && (
            <p style={styles.hint}>این رونویسی هنوز در حال پردازش است.</p>
          )}
        </div>
      );
    }

    const ai = transcript.ai_outputs;

    switch (activeTab) {
      case 'summary':
        return (
          <div>
            <div style={styles.aiCard}>
              <h4 style={styles.aiCardTitle}>خلاصه کوتاه</h4>
              <p style={styles.aiText}>{ai.short_summary}</p>
            </div>
            <div style={styles.aiCard}>
              <h4 style={styles.aiCardTitle}>خلاصه مفصل</h4>
              <p style={styles.aiText}>{ai.detailed_summary}</p>
            </div>
          </div>
        );
      case 'tasks':
        return (
          <div style={styles.taskList}>
            {ai.tasks.map((task, i) => (
              <div key={i} style={styles.taskItem}>
                <span style={styles.taskBullet}>{i + 1}.</span>
                <span style={styles.taskText}>{task}</span>
              </div>
            ))}
          </div>
        );
      case 'translation':
        return (
          <div style={styles.aiCard}>
            <p style={styles.aiText}>{ai.english_translation}</p>
          </div>
        );
      case 'chapters':
        return (
          <div style={styles.chapterList}>
            {ai.chapters.map((ch, i) => (
              <div key={i} style={styles.chapterItem}>
                <div style={styles.chapterHeader}>
                  <span style={styles.chapterNumber}>{i + 1}</span>
                  <span style={styles.chapterTitle}>{ch.title}</span>
                  <span style={styles.chapterTime}>{formatDuration(ch.timestamp_sec)}</span>
                </div>
                <p style={styles.chapterContent}>{ch.content}</p>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/history')} style={styles.backNav}>
        ← بازگشت به تاریخچه
      </button>

      {isDemo && (
        <div style={styles.demoBanner}>
          <span>🔧 حالت نمایشی - سرویس پشتیبان فعال نیست</span>
        </div>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>{transcript.title}</h2>
        <div style={styles.metaRow}>
          <span style={styles.typeBadge}>{getTypeLabel(transcript.type)}</span>
          <span style={styles.metaItem}>{formatDateTime(transcript.created_at)}</span>
          <span style={styles.metaItem}>{formatDuration(transcript.duration_sec)}</span>
        </div>
        <span style={{
          ...styles.statusBadge,
          backgroundColor: transcript.status === 'completed' ? '#e8f5e9' : transcript.status === 'processing' ? '#fff3e0' : '#ffebee',
          color: transcript.status === 'completed' ? '#2e7d32' : transcript.status === 'processing' ? '#f57c00' : '#c62828',
        }}>
          {getStatusLabel(transcript.status)}
        </span>
      </div>

      {transcript.transcript_text && (
        <div style={styles.transcriptSection}>
          <h3 style={styles.sectionTitle}>متن رونویسی</h3>
          <div style={styles.transcriptBox}>
            <p style={styles.transcriptText}>{transcript.transcript_text}</p>
          </div>
        </div>
      )}

      {hasAiOutputs && (
        <div style={styles.aiSection}>
          <h3 style={styles.sectionTitle}>خروجی هوش مصنوعی</h3>
          <div style={styles.tabRow}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === tab.key ? styles.tabButtonActive : {}),
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div style={styles.tabContent}>
            {renderAiTab()}
          </div>
        </div>
      )}

      {!isBackendAvailable && hasAiOutputs && (
        <div style={styles.regenerateBanner}>
          <p style={styles.regenerateText}>
            برای بازتولید خروجی‌ها یا دریافت خروجی‌های جدید، سرویس پشتیبان باید فعال باشد.
          </p>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1.5rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  backButton: {
    padding: '0.6rem 1.5rem',
    backgroundColor: '#646cff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  backNav: {
    background: 'none',
    border: 'none',
    color: '#646cff',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0.5rem 0',
    marginBottom: '0.5rem',
  },
  demoBanner: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    marginBottom: '1rem',
    fontSize: '0.8rem',
    color: '#f57c00',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.75rem 0',
  },
  metaRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '0.5rem',
  },
  typeBadge: {
    fontSize: '0.8rem',
  },
  metaItem: {
    fontSize: '0.75rem',
    color: '#999',
    direction: 'ltr',
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
  },
  transcriptSection: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.75rem 0',
  },
  transcriptBox: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
  },
  transcriptText: {
    fontSize: '0.875rem',
    lineHeight: 1.8,
    color: '#333',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  aiSection: {
    marginBottom: '1.5rem',
  },
  tabRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  tabButton: {
    flex: 1,
    padding: '0.5rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#f0f0f0',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabButtonActive: {
    backgroundColor: '#646cff',
    color: '#fff',
  },
  tabContent: {
    minHeight: '100px',
  },
  aiCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  aiCardTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#646cff',
    margin: '0 0 0.5rem 0',
  },
  aiText: {
    fontSize: '0.85rem',
    lineHeight: 1.7,
    color: '#333',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  taskItem: {
    display: 'flex',
    gap: '0.5rem',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    padding: '0.75rem 1rem',
    alignItems: 'flex-start',
  },
  taskBullet: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#646cff',
    minWidth: '1.5rem',
  },
  taskText: {
    fontSize: '0.85rem',
    color: '#333',
    lineHeight: 1.6,
  },
  chapterList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  chapterItem: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
  },
  chapterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  chapterNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#646cff',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  chapterTitle: {
    flex: 1,
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1a1a1a',
  },
  chapterTime: {
    fontSize: '0.75rem',
    color: '#999',
    direction: 'ltr',
  },
  chapterContent: {
    fontSize: '0.8rem',
    lineHeight: 1.6,
    color: '#555',
    margin: 0,
  },
  emptySection: {
    textAlign: 'center',
    padding: '2rem 1rem',
    color: '#999',
    fontSize: '0.875rem',
  },
  hint: {
    marginTop: '0.5rem',
    fontSize: '0.8rem',
    color: '#f57c00',
  },
  regenerateBanner: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    textAlign: 'center',
  },
  regenerateText: {
    fontSize: '0.8rem',
    color: '#999',
    margin: 0,
  },
};
